import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import RecordRTC from "recordrtc";
import { FaceDetection } from "@mediapipe/face_detection";
import { Camera } from "@mediapipe/camera_utils";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Navbar from "./Navbar";
import "../App.css";


const RECORD_SECONDS = 30;
const SQI_THRESHOLD = 0.5;


const speak = (text, lang) => {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();

  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = lang === "hi" ? "hi-IN" : "en-US";
  utter.rate = 0.9;
  utter.pitch = 1;
  utter.volume = 1;

  window.speechSynthesis.speak(utter);
};

const generateSpeechText = (v, lang) => {
  if (!v) return "";
  const message=getMedicalInsights(v, lang)
  console.log(message,"message")
  if (lang === "hi") {
    


    return `
आपकी स्वास्थ्य रिपोर्ट तैयार है।
आपकी हृदय गति ${v.heartRate} प्रति मिनट है।
${message[0].title}
${message[0].text}

यह जानकारी केवल जागरूकता के लिए है, यह मेडिकल सलाह नहीं है।
`;
  }

  return `
Your health report is ready.
${message[0].title}
${message[0].text}

This information is for awareness only and not a medical diagnosis.
`;
};


/* ================= LANGUAGE TEXT ================= */

const HRV_TEXT = {
  en: {
    LOW: "Low",
    HEALTHY: "Healthy",
    OPTIMAL: "Optimal",

    RELAXED: "Relaxed",
    BALANCED: "Balanced",
    STRESSED: "Stressed",

    SDNN_DESC:
      "The standard deviation of all NN intervals (time between each heartbeat). Higher values usually indicate that your body is coping better with stress.",

    RMSSD_DESC:
      "The root mean square of successive intervals between normal heartbeats. Higher values indicate better recovery and a relaxed nervous system.",

    LFHF_DESC:
      "The LF/HF ratio reflects the balance between stress (fight-or-flight) and relaxation (rest-and-digest).",
  },

  hi: {
    LOW: "कम",
    HEALTHY: "सामान्य",
    OPTIMAL: "उत्तम",

    RELAXED: "आराम",
    BALANCED: "संतुलित",
    STRESSED: "तनाव",

    SDNN_DESC:
      "यह प्रत्येक दिल की धड़कन के बीच के समय का मानक विचलन दर्शाता है। अधिक मान यह दर्शाता है कि शरीर तनाव को बेहतर तरीके से संभाल रहा है।",

    RMSSD_DESC:
      "यह लगातार दिल की धड़कनों के बीच के अंतर को दर्शाता है। अधिक मान बेहतर रिकवरी और शांत नर्वस सिस्टम को दर्शाता है।",

    LFHF_DESC:
      "यह तनाव (फाइट-या-फ्लाइट) और आराम (रेस्ट-एंड-डाइजेस्ट) के बीच संतुलन को दर्शाता है।",
  },
};


const DISCLAIMER_TEXT = {
  en: {
    header: "⚠️ Important Health Disclaimer",
    intro: (
      <>
        The following HRV (Heart Rate Variability) information is provided for
        <strong> general awareness and educational purposes only</strong>.
        It is <strong>not a medical diagnosis or treatment</strong>.
      </>
    ),

    rmssd: {
      title: "RMSSD",
      points: [
        { text: "Below 20 ms – Reduced recovery", color: "yellow" },
        { text: "20–50 ms – Normal range", color: "green" },
        { text: "Above 50 ms – Good recovery", color: "darkgreen" },
      ],
    },

    sdnn: {
      title: "SDNN",
      points: [
        { text: "Below 50 ms – Higher risk", color: "red" },
        { text: "50–100 ms – Moderate range", color: "yellow" },
        { text: "Above 100 ms – Healthy variability", color: "green" },
      ],
    },

    footer:
      "⚕️ Always consult a qualified healthcare professional for medical advice.",

    ctaTitle: "🌐 Manage Your Health Better with AICA",
    ctaDesc:
      "Take better care of yourself and your loved ones with 24×7 AI-powered health support.",
    ctaBtn: "Visit qcares.aicahealth.com →",
    ctaSub:
      "AICA acts as your Personal Care Assistant, helping you get the right guidance for your healthcare needs — anytime, anywhere, even when a doctor is not nearby.",
  },

  hi: {
    header: "⚠️ महत्वपूर्ण स्वास्थ्य सूचना",
    intro: (
      <>
        नीचे दी गई HRV (हृदय गति परिवर्तनशीलता) जानकारी केवल
        <strong> सामान्य जागरूकता और शैक्षिक उद्देश्य</strong> के लिए है।
        यह <strong>मेडिकल निदान या उपचार नहीं</strong> है।
      </>
    ),

    rmssd: {
      title: "RMSSD",
      points: [
        { text: "20 ms से कम – रिकवरी कम हो सकती है", color: "yellow" },
        { text: "20–50 ms – सामान्य सीमा", color: "green" },
        { text: "50 ms से अधिक – अच्छी रिकवरी", color: "darkgreen" },
      ],
    },

    sdnn: {
      title: "SDNN",
      points: [
        { text: "50 ms से कम – अधिक जोखिम", color: "red" },
        { text: "50–100 ms – मध्यम स्तर", color: "yellow" },
        { text: "100 ms से अधिक – स्वस्थ परिवर्तनशीलता", color: "green" },
      ],
    },

    footer:
      "⚕️ स्वास्थ्य से संबंधित किसी भी निर्णय के लिए कृपया योग्य चिकित्सक से परामर्श लें।",

    ctaTitle: "🌐 AICA के साथ अपने स्वास्थ्य को बेहतर प्रबंधित करें",
    ctaDesc:
      "अपने और अपने परिवार के स्वास्थ्य की बेहतर देखभाल करें 24×7 AI आधारित सहायता के साथ।",
    ctaBtn: "qcares.aicahealth.com पर जाएँ →",
    ctaSub:
      "AICA आपका पर्सनल केयर असिस्टेंट है, जो आपको स्वास्थ्य संबंधी सही मार्गदर्शन देता है — कभी भी, कहीं भी, जब डॉक्टर पास न हों।",
  },
};



const MEDICAL_TEXT = {
  en: {
    startScan: "Start 30s Scan",
    analyze: "Analyze My Health",
    analyzing: "Analyzing...",
    recordAgain: "Record Again",
    scanAgain: "Scan Again",
    report: "Your Health Report",
    hr: "Heart Rate",
    bp: "Blood Pressure",

    disclaimer: `⚠️ Important Disclaimer

The following HRV interpretation ranges are provided for general awareness and educational purposes only. 
They are not intended for medical diagnosis or treatment.

RMSSD (Root Mean Square of Successive Differences):
• < 20 ms  → Reduced parasympathetic activity (potential risk)
• 20–50 ms → Typical / normal range
• > 50 ms  → Good parasympathetic (vagal) function

SDNN (Standard Deviation of NN Intervals):
• < 50 ms   → Higher risk (associated with reduced HRV)
• 50–100 ms → Moderate risk range
• > 100 ms  → Lower risk (generally healthy autonomic function)

⚠️ This information does not replace professional medical advice.
Please consult a qualified healthcare professional for clinical evaluation.`,

    LOW_BP: {
      title: "Low Blood Pressure",
      text: "Your blood pressure appears low (below 90/60 mmHg). This may cause dizziness or fatigue.",
    },

    CRISIS: {
      title: "Hypertensive Crisis",
      text: "Blood pressure above 180/120 mmHg is a medical emergency. Seek immediate medical care.",
    },

    ELEVATED: {
      title: "Elevated Blood Pressure",
      text: "Your blood pressure is slightly elevated. Lifestyle changes may help.",
    },

    STAGE_1: {
      title: "Hypertension – Stage 1",
      text: "Mildly elevated blood pressure. Monitoring and lifestyle changes are advised.",
    },

    STAGE_2: {
      title: "Hypertension – Stage 2",
      text: "High blood pressure detected. Please consult a doctor.",
    },

    GOOD_HEART: {
      title: "Good Heart Health",
      text: "Healthy HRV indicate good cardiovascular balance.",
    },

    LOW_HRV: {
      title: "Low HRV",
      text: "Low HRV may be associated with stress, fatigue, or reduced recovery.",
    },

    LOW_HRV_BP: {
      title: "Low HRV",
      text: "Combined indicators suggest increased cardiovascular strain. Medical consultation is recommended.",
    },
  },

  hi: {
    startScan: "30 सेकंड स्कैन शुरू करें",
    analyze: "स्वास्थ्य विश्लेषण करें",
    analyzing: "विश्लेषण हो रहा है...",
    recordAgain: "फिर से रिकॉर्ड करें",
    scanAgain: "फिर से स्कैन करें",
    report: "आपकी स्वास्थ्य रिपोर्ट",
    hr: "हृदय गति",
    bp: "ब्लड प्रेशर",
    disclaimer: `⚠️ महत्वपूर्ण सूचना

नीचे दिए गए HRV (Heart Rate Variability) मान केवल सामान्य जागरूकता और शैक्षिक उद्देश्य के लिए हैं।
यह जानकारी किसी भी प्रकार के मेडिकल निदान या उपचार का विकल्प नहीं है।

RMSSD (रूट मीन स्क्वेयर ऑफ सक्सेसिव डिफरेंसेज़):
• 20 ms से कम  → पैरासिम्पैथेटिक गतिविधि कम हो सकती है (संभावित जोखिम)
• 20–50 ms     → सामान्य सीमा
• 50 ms से अधिक → अच्छी पैरासिम्पैथेटिक (वागल) कार्यक्षमता

SDNN (NN इंटरवल का मानक विचलन):
• 50 ms से कम   → अधिक जोखिम (HRV कम होने से जुड़ा)
• 50–100 ms     → मध्यम जोखिम
• 100 ms से अधिक → कम जोखिम (आमतौर पर अच्छी ऑटोनोमिक कार्यक्षमता)

⚠️ यह जानकारी पेशेवर चिकित्सा सलाह का विकल्प नहीं है।
स्वास्थ्य से संबंधित किसी भी निर्णय के लिए कृपया योग्य चिकित्सक से परामर्श लें।`,


    LOW_BP: {
      title: "लो ब्लड प्रेशर",
      text: "आपका ब्लड प्रेशर कम लग रहा है। इससे चक्कर या कमजोरी हो सकती है।",
    },

    CRISIS: {
      title: "हाइपरटेंसिव क्राइसिस",
      text: "180/120 mmHg से अधिक BP मेडिकल इमरजेंसी है। तुरंत डॉक्टर से संपर्क करें।",
    },

    ELEVATED: {
      title: "थोड़ा बढ़ा हुआ BP",
      text: "आपका ब्लड प्रेशर थोड़ा बढ़ा हुआ है। जीवनशैली में सुधार मदद कर सकता है।",
    },

    STAGE_1: {
      title: "हाइपरटेंशन – स्टेज 1",
      text: "ब्लड प्रेशर थोड़ा अधिक है। निगरानी आवश्यक है।",
    },

    STAGE_2: {
      title: "हाइपरटेंशन – स्टेज 2",
      text: "ब्लड प्रेशर काफी अधिक है। डॉक्टर से सलाह लें।",
    },

    GOOD_HEART: {
      title: "अच्छा हार्ट हेल्थ",
      text: "HRV अच्छा है, जो हृदय स्वास्थ्य को दर्शाता है।",
    },

    LOW_HRV: {
      title: "लो HRV",
      text: "तनाव, थकान या रिकवरी कम होने का संकेत हो सकता है।",
    },

    LOW_HRV_BP: {
      title: "लो HRV और हाई BP",
      text: "संयुक्त जोखिम संकेत पाए गए। डॉक्टर से संपर्क करें।",
    },
  },
};

/* ================= MEDICAL LOGIC ================= */

function classifyBP(sbp, dbp) {
  if (sbp < 90 || dbp < 60) return "LOW";
  if (sbp >= 180 || dbp >= 120) return "CRISIS";
  if (sbp >= 140 || dbp >= 90) return "STAGE_2";
  if ((sbp >= 130 && sbp <= 139) || (dbp >= 80 && dbp <= 89)) return "STAGE_1";
  if (sbp >= 120 && sbp <= 129 && dbp < 80) return "ELEVATED";
  return "NORMAL";
}

function classifyHRV(SDNN, RMSSD) {
  if (SDNN >= 50 && RMSSD >= 50) return "HIGH";
  if (SDNN < 50 && RMSSD < 20) return "LOW";
  return "MODERATE";
}


function getMedicalInsights(v, lang) {
  if (!v) return [];
  const T = MEDICAL_TEXT[lang];
  const hrv = classifyHRV(v.SDNN, v.RMSSD);

  const messages = [];

  if (hrv === "HIGH")
    messages.push({ type: "success", ...T.GOOD_HEART });

  if (hrv === "LOW")
    messages.push({ type: "danger", ...T.LOW_HRV_BP });

  if (hrv === "LOW")
    messages.push({ type: "warning", ...T.LOW_HRV });

  return messages;
}

/* ================= APP ================= */

export default function App() {
  const webcamRef = useRef(null);
  const recorderRef = useRef(null);
  const navigate = useNavigate();

  const [faceDetected, setFaceDetected] = useState(false);
  const faceDetectedRef = useRef(false);
  const [faceError, setFaceError] = useState(false);
  const [sqiError, setSqiError] = useState(false);

  const [lang, setLang] = useState("en");
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(RECORD_SECONDS);
  const [videoBlob, setVideoBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [vitals, setVitals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [waveGraphData, setWaveGraphData] = useState([]);

  const t = HRV_TEXT[lang];

  useEffect(() => {
    if (vitals) {
      const speechText = generateSpeechText(vitals, lang);
      console.log(speechText, "speechText")
      speak(speechText, lang);
    }
  }, [vitals, lang]);


   useEffect(() => {
    let camera;

    const initFaceDetection = () => {
      if (!webcamRef.current?.video || webcamRef.current.video.readyState !== 4) return;

      const video = webcamRef.current.video;

      const faceDetection = new FaceDetection({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`,
      });

      faceDetection.setOptions({
        model: "short",
        minDetectionConfidence: 0.5,
      });

      faceDetection.onResults((results) => {
        const detected = results.detections?.length > 0;
        faceDetectedRef.current = detected;
        setFaceDetected(detected);
      });

      camera = new Camera(video, {
        onFrame: async () => {
          if (video) await faceDetection.send({ image: video });
        },
        width: 640,
        height: 480,
      });

      camera.start();
    };

    const interval = setInterval(() => {
      if (webcamRef.current?.video?.readyState === 4) {
        clearInterval(interval);
        initFaceDetection();
      }
    }, 500);

    return () => {
      if (camera) camera.stop();
      clearInterval(interval);
    };
  }, []);




  const mapLabel = (label) =>
    label === "Low"
      ? t.LOW
      : label === "Healthy"
        ? t.HEALTHY
        : t.OPTIMAL;


 

  const getSDNNStatus = (v) => {
    console.log(v, "vv0999999999999999")
    if (v < 30) return { label: "Low", active: "Low", color: "#fbbc05" };
    if (v <= 96) return { label: "Healthy", active: "Healthy", color: "#34a853" };
    return { label: "Optimal", active: "Optimal", color: "#0f9d58" };
  };



  const getRMSSDStatus = (v) => {
    if (v < 20) return { label: "Low", active: "Low", color: "#fbbc05" };
    if (v <= 90) return { label: "Healthy", active: "Healthy", color: "#34a853" };
    return { label: "Optimal", active: "Optimal", color: "#0f9d58" };
  };

  const getLFHFStatus = (lfhf) => {
  if (lfhf < 0.5) {
    // Very low LF/HF → dominant parasympathetic → relaxed
    return { label: "Optimal", active: "Relaxed", color: "#0f9d58" };
  } else if (lfhf >= 0.5 && lfhf <= 1.5) {
    // Balanced sympathetic/parasympathetic activity
    return { label: "Healthy", active: "Balanced", color: "#34a853" };
  } else { 
    // High LF/HF → dominant sympathetic → stressed
    return { label: "High Stress", active: "Stressed", color: "#ea4335" };
  }
};

  const formatNum = (v, digits = 0) =>
    typeof v === "number" && !isNaN(v) ? v.toFixed(digits) : "--";

const startRecording = () => {
    if (!faceDetectedRef.current) {
      alert("Face Not Detected. Please look at the camera.");
      return;
    }

    setFaceError(false);
    setSqiError(false);
    setVitals(null);

    const stream = webcamRef.current?.stream;
    if (!stream) return alert("Webcam not available");

    recorderRef.current = new RecordRTC(stream, {
  type: "video",
  mimeType: "video/mp4",
  bitsPerSecond: 8000000,   // increase bitrate
});

    recorderRef.current.startRecording();
    setIsRecording(true);

    let t = RECORD_SECONDS;
    setTimer(t);

    const interval = setInterval(() => {
      t -= 1;
      setTimer(t);

      if (!faceDetectedRef.current) {
        clearInterval(interval);
        recorderRef.current.stopRecording(() => recorderRef.current.destroy());
        setIsRecording(false);
        setFaceError(true);
        return;
      }

      if (t <= 0) {
        clearInterval(interval);
        stopRecording();
      }
    }, 1000);
  };

  const stopRecording = () => {
    recorderRef.current.stopRecording(() => {
      const blob = recorderRef.current.getBlob();
      setVideoBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setIsRecording(false);
    });
  };

  /* ===== HANDLE SUBMIT ===== */

  const handleSubmit = async () => {
    if (!videoBlob) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("file", videoBlob);
    formData.append("patient_id", localStorage.getItem("patient_id"))

    try {
      const res = await fetch(
        "https://lovencare.gopillz.com/api/analyze",
        { method: "POST", body: formData }
      );

      const data = await res.json();
      console.log(data, "data")
      const sqi = data?.sqi ?? 0;

      if (sqi < SQI_THRESHOLD) {
        setSqiError(true);
        setVideoBlob(null);
        setPreviewUrl("");
        setLoading(false);
        return;
      }

       setWaveGraphData(
          (data.waveform || []).map((v, i) => ({ time: i, value: v }))
        );

      setVitals({
          heartRate: Math.round(data.heart_rate_bpm_final || 0),
          SDNN: data?.hrv_time?.SDNN_ms || 0,
          RMSSD: data?.hrv_time?.RMSSD_ms || 0,
          LFHF: data?.hrv_freq?.["LF/HF"] || 0,
          rr:data?.rr??null,
          blood_pressure:data?.blood_pressure??null


      })

    } catch (err) {
      alert("Upload failed");
    }

    setLoading(false);
  };


  const T = MEDICAL_TEXT[lang];
  const D = DISCLAIMER_TEXT[lang]; 

  return (

      <div className="container">
        <Navbar />

      <div className="lang-toggle">
        <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>English</button>
        <button className={lang === "hi" ? "active" : ""} onClick={() => setLang("hi")}>हिन्दी</button>
      </div>

      {!videoBlob && (
          <div className="rppg-card">
  <div className="camera-wrapper">
    <Webcam
  ref={webcamRef}
  className="rppg-webcam"
  videoConstraints={{
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30, min: 30 },
  facingMode: "user"
}}
/>

    {!faceDetected && (
      <div className="face-overlay">
        ⚠ Face Not Detected
      </div>
    )}
  </div>

  {!isRecording ? (
    <button
      className="primary-btn"
      onClick={startRecording}
      disabled={!faceDetected}
    >
      ▶ Start 30s Scan
    </button>
  ) : (
    <div className="timer-circle">{timer}s</div>
  )}
</div>

        )}

        {videoBlob && !vitals && (
          <div className="rppg-card">
            <video src={previewUrl} controls className="rppg-preview" />
            <button
              className="primary-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Analyzing..." : "Analyze My Health"}
            </button>
          </div>
        )}

        {sqiError && (
          <div className="alert-box">
            ⚠ Signal quality too low. Use better lighting and stay still.
          </div>
        )}
        
        {vitals && (
          <div className="card">
            <h2>{T.report}</h2>

            <div className="vitals-grid">
              <div className="vital-box">❤️ {vitals.heartRate} <small>{T.hr}</small></div> 
              <div className="vital-box">
  🫁 {vitals.rr ? vitals.rr.toFixed(1) : "--"} 
  <small>Respiratory Rate (bpm)</small>
</div>
               {/* <div className="vital-box">🫁 {vitals.spo2}% <small>SpO₂</small></div> */}
              <div className="vital-box">🩸 {vitals.blood_pressure.sbp}/{vitals.blood_pressure.dbp} <small>{T.bp}</small></div>
            </div>

            {getMedicalInsights(vitals, lang).map((m, i) => (
              <div key={i} className={`alert ${m.type}`}>
                <strong>{m.title}</strong>
                <p>{m.text}</p>
              </div>
            ))}

            {(() => {
              const s = getSDNNStatus(vitals.SDNN);

              return (
                <div className="health-card">
                  <span className="pill" style={{ background: s.color }}>
                    {mapLabel(s.label)}
                  </span>

                  <h2>{formatNum(vitals.SDNN)} <span>ms</span></h2>

                  <div className="scale">
                    <span className={s.active === "Low" ? "active" : ""}>{t.LOW}</span>
                    <span className={s.active === "Healthy" ? "active" : ""}>{t.HEALTHY}</span>
                    <span className={s.active === "Optimal" ? "active" : ""}>{t.OPTIMAL}</span>
                  </div>

                  <small>{t.SDNN_DESC}</small>
                </div>
              );
            })()}


            {/* RMSSD */}
            {(() => {
              const s = getRMSSDStatus(vitals.RMSSD);

              return (
                <div className="health-card">
                  <span className="pill" style={{ background: s.color }}>
                    {mapLabel(s.label)}
                  </span>

                  <h2>{formatNum(vitals.RMSSD)} <span>ms</span></h2>

                  <div className="scale">
                    <span className={s.active === "Low" ? "active" : ""}>{t.LOW}</span>
                    <span className={s.active === "Healthy" ? "active" : ""}>{t.HEALTHY}</span>
                    <span className={s.active === "Optimal" ? "active" : ""}>{t.OPTIMAL}</span>
                  </div>

                  <small>{t.RMSSD_DESC}</small>
                </div>
              );
            })()}

            {/* {console.log(vitals.LFHF)} */}
            {Number.isFinite(Number(vitals.LFHF)) && Number(vitals.LFHF) > 0 && 
            (() => {
              console.log(vitals.LFHF, "vitdddals.LFHF")
              const s = getLFHFStatus(vitals.LFHF);

              return (
                <div className="health-card">
                  <span className="pill" style={{ background: s.color }}>
                    {s.active === "Relaxed"
                      ? t.RELAXED
                      : s.active === "Balanced"
                        ? t.BALANCED
                        : t.STRESSED}
                  </span>

                  <h2>{formatNum(vitals.LFHF, 2)}</h2>

                  <div className="scale">
                    <span className={s.active === "Relaxed" ? "active" : ""}>{t.RELAXED}</span>
                    <span className={s.active === "Balanced" ? "active" : ""}>{t.BALANCED}</span>
                    <span className={s.active === "Stressed" ? "active" : ""}>{t.STRESSED}</span>
                  </div>

                  <small>{t.LFHF_DESC}</small>
                </div>
              );
            })()}




            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={waveGraphData}>
                <Line dataKey="value" dot={false} />
                <XAxis hide />
                <YAxis hide />
                <Tooltip />
              </LineChart>
            </ResponsiveContainer>

            
            
{
 
  <div className="disclaimer-card">

  <div className="disclaimer-header">{D.header}</div>

  <div className="disclaimer-section">
    <p>{D.intro}</p>
  </div>

  <div className="disclaimer-section">
    <h4>{D.rmssd.title}</h4>
    <ul>
      {D.rmssd.points.map((item, i) => (
        <li key={i}>
          <span className={`dot ${item.color}`} />
          {item.text}
        </li>
      ))}
    </ul>
  </div>

  <div className="disclaimer-section">
    <h4>{D.sdnn.title}</h4>
    <ul>
      {D.sdnn.points.map((item, i) => (
        <li key={i}>
          <span className={`dot ${item.color}`} />
          {item.text}
        </li>
      ))}
    </ul>
  </div>

  <div className="disclaimer-footer">{D.footer}</div>

  {/* CTA SECTION */}
  <div className="cta-section">
    <h3>{D.ctaTitle}</h3>
    <p>{D.ctaDesc}</p>

    <a
      href="https://qcares.aicahealth.com"
      target="_blank"
      rel="noopener noreferrer"
      className="cta-button"
    >
      {D.ctaBtn}
    </a>

    <p className="cta-subtext">{D.ctaSub}</p>
  </div>
</div>
}


            <button  onClick={() => {
    // clear auth
    localStorage.removeItem("token");
    localStorage.removeItem("patient_id");

    // redirect to login
    navigate("/login");
  }}>
              🔁 {T.scanAgain}
            </button>
          </div>
        )}
      </div>

  );
}




// import React, { useRef, useState, useEffect } from "react";
// import Webcam from "react-webcam";
// import RecordRTC from "recordrtc";
// import {
//   LineChart,
//   Line,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
// } from "recharts";
// import Navbar from "./Navbar";
// import "../App.css";

// const RECORD_SECONDS = 30;


// const speak = (text, lang) => {
//   if (!window.speechSynthesis || !text) return;
//   window.speechSynthesis.cancel();

//   const utter = new SpeechSynthesisUtterance(text);
//   utter.lang = lang === "hi" ? "hi-IN" : "en-US";
//   utter.rate = 0.9;
//   utter.pitch = 1;
//   utter.volume = 1;

//   window.speechSynthesis.speak(utter);
// };

// const generateSpeechText = (v, lang) => {
//   if (!v) return "";
//   const message=getMedicalInsights(v, lang)
//   console.log(message,"message")
//   if (lang === "hi") {
    


//     return `
// आपकी स्वास्थ्य रिपोर्ट तैयार है।
// आपकी हृदय गति ${v.heartRate} प्रति मिनट है।
// आपका ऑक्सीजन स्तर ${v.spo2} प्रतिशत है।
// आपका ब्लड प्रेशर ${v.sbp} / ${v.dbp} है।
// ${message[0].title}
// ${message[0].text}

// यह जानकारी केवल जागरूकता के लिए है, यह मेडिकल सलाह नहीं है।
// `;
//   }

//   return `
// Your health report is ready.
// Your heart rate is ${v.heartRate} beats per minute.
// Your oxygen level is ${v.spo2} percent.
// Your blood pressure is ${v.sbp} over ${v.dbp}.
// ${message[0].title}
// ${message[0].text}

// This information is for awareness only and not a medical diagnosis.
// `;
// };


// /* ================= LANGUAGE TEXT ================= */

// const HRV_TEXT = {
//   en: {
//     LOW: "Low",
//     HEALTHY: "Healthy",
//     OPTIMAL: "Optimal",

//     RELAXED: "Relaxed",
//     BALANCED: "Balanced",
//     STRESSED: "Stressed",

//     SDNN_DESC:
//       "The standard deviation of all NN intervals (time between each heartbeat). Higher values usually indicate that your body is coping better with stress.",

//     RMSSD_DESC:
//       "The root mean square of successive intervals between normal heartbeats. Higher values indicate better recovery and a relaxed nervous system.",

//     LFHF_DESC:
//       "The LF/HF ratio reflects the balance between stress (fight-or-flight) and relaxation (rest-and-digest).",
//   },

//   hi: {
//     LOW: "कम",
//     HEALTHY: "सामान्य",
//     OPTIMAL: "उत्तम",

//     RELAXED: "आराम",
//     BALANCED: "संतुलित",
//     STRESSED: "तनाव",

//     SDNN_DESC:
//       "यह प्रत्येक दिल की धड़कन के बीच के समय का मानक विचलन दर्शाता है। अधिक मान यह दर्शाता है कि शरीर तनाव को बेहतर तरीके से संभाल रहा है।",

//     RMSSD_DESC:
//       "यह लगातार दिल की धड़कनों के बीच के अंतर को दर्शाता है। अधिक मान बेहतर रिकवरी और शांत नर्वस सिस्टम को दर्शाता है।",

//     LFHF_DESC:
//       "यह तनाव (फाइट-या-फ्लाइट) और आराम (रेस्ट-एंड-डाइजेस्ट) के बीच संतुलन को दर्शाता है।",
//   },
// };


// const MEDICAL_TEXT = {
//   en: {
//     startScan: "Start 30s Scan",
//     analyze: "Analyze My Health",
//     analyzing: "Analyzing...",
//     recordAgain: "Record Again",
//     scanAgain: "Scan Again",
//     report: "Your Health Report",
//     hr: "Heart Rate",
//     bp: "Blood Pressure",
//     disclaimer:
//       "⚠️ This information is for awareness only and is not a medical diagnosis. Please consult a healthcare professional.",

//     LOW_BP: {
//       title: "Low Blood Pressure",
//       text:
//         "Your BP looks low (below 90/60 mmHg). This may cause dizziness or fatigue.",
//     },
//     CRISIS: {
//       title: "Hypertensive Crisis",
//       text:
//         "BP above 180/120 mmHg is a medical emergency. Seek immediate care.",
//     },
//     ELEVATED: {
//       title: "Elevated Blood Pressure",
//       text: "Your BP is slightly elevated. Lifestyle changes may help.",
//     },
//     STAGE_1: {
//       title: "Hypertension – Stage 1",
//       text: "Mildly high BP. Monitoring and lifestyle changes advised.",
//     },
//     STAGE_2: {
//       title: "Hypertension – Stage 2",
//       text: "High BP detected. Please consult a doctor.",
//     },
//     GOOD_HEART: {
//       title: "Good Heart Health",
//       text: "Normal BP and good HRV indicate a healthy heart.",
//     },
//     LOW_HRV: {
//       title: "Low HRV",
//       text: "Low HRV may indicate stress or fatigue.",
//     },
//     LOW_HRV_BP: {
//       title: "Low HRV & High BP",
//       text: "Combined risk indicators detected. Consult a doctor.",
//     },
//   },

//   hi: {
//     startScan: "30 सेकंड स्कैन शुरू करें",
//     analyze: "स्वास्थ्य विश्लेषण करें",
//     analyzing: "विश्लेषण हो रहा है...",
//     recordAgain: "फिर से रिकॉर्ड करें",
//     scanAgain: "फिर से स्कैन करें",
//     report: "आपकी स्वास्थ्य रिपोर्ट",
//     hr: "हृदय गति",
//     bp: "ब्लड प्रेशर",
//     disclaimer:
//       "⚠️ यह जानकारी केवल जागरूकता के लिए है, यह मेडिकल निदान नहीं है। डॉक्टर से सलाह लें।",

//     LOW_BP: {
//       title: "लो ब्लड प्रेशर",
//       text: "आपका BP कम है। चक्कर या कमजोरी हो सकती है।",
//     },
//     CRISIS: {
//       title: "हाईपरटेंसिव क्राइसिस",
//       text: "180/120 mmHg से ऊपर BP इमरजेंसी है।",
//     },
//     ELEVATED: {
//       title: "थोड़ा बढ़ा हुआ BP",
//       text: "आपका BP थोड़ा बढ़ा हुआ है।",
//     },
//     STAGE_1: {
//       title: "हाइपरटेंशन – स्टेज 1",
//       text: "BP थोड़ा ज्यादा है। निगरानी जरूरी है।",
//     },
//     STAGE_2: {
//       title: "हाइपरटेंशन – स्टेज 2",
//       text: "BP काफी ज्यादा है। डॉक्टर से सलाह लें।",
//     },
//     GOOD_HEART: {
//       title: "अच्छा हार्ट हेल्थ",
//       text: "BP सामान्य है और HRV अच्छा है।",
//     },
//     LOW_HRV: {
//       title: "लो HRV",
//       text: "तनाव या थकान का संकेत।",
//     },
//     LOW_HRV_BP: {
//       title: "लो HRV और हाई BP",
//       text: "जोखिम संकेत। डॉक्टर से संपर्क करें।",
//     },
//   },
// };

// /* ================= MEDICAL LOGIC ================= */

// function classifyBP(sbp, dbp) {
//   if (sbp < 90 || dbp < 60) return "LOW";
//   if (sbp >= 180 || dbp >= 120) return "CRISIS";
//   if (sbp >= 140 || dbp >= 90) return "STAGE_2";
//   if ((sbp >= 130 && sbp <= 139) || (dbp >= 80 && dbp <= 89)) return "STAGE_1";
//   if (sbp >= 120 && sbp <= 129 && dbp < 80) return "ELEVATED";
//   return "NORMAL";
// }

// function classifyHRV(SDNN, RMSSD) {
//   if (SDNN >= 100 && RMSSD >= 50) return "HIGH";
//   if (SDNN < 50 && RMSSD < 30) return "LOW";
//   return "MODERATE";
// }

// function getMedicalInsights(v, lang) {
//   if (!v) return [];
//   const T = MEDICAL_TEXT[lang];
//   const bp = classifyBP(v.sbp, v.dbp);
//   const hrv = classifyHRV(v.SDNN, v.RMSSD);

//   const messages = [];

//   if (bp === "LOW") return [{ type: "warning", ...T.LOW_BP }];
//   if (bp === "CRISIS") return [{ type: "danger", ...T.CRISIS }];
//   if (bp === "ELEVATED") messages.push({ type: "warning", ...T.ELEVATED });
//   if (bp === "STAGE_1") messages.push({ type: "warning", ...T.STAGE_1 });
//   if (bp === "STAGE_2") messages.push({ type: "danger", ...T.STAGE_2 });

//   if (hrv === "HIGH" && bp === "NORMAL")
//     messages.push({ type: "success", ...T.GOOD_HEART });

//   if (hrv === "LOW" && bp !== "NORMAL")
//     messages.push({ type: "danger", ...T.LOW_HRV_BP });

//   if (hrv === "LOW" && bp === "NORMAL")
//     messages.push({ type: "warning", ...T.LOW_HRV });

//   return messages;
// }

// /* ================= APP ================= */

// export default function App() {
//   const webcamRef = useRef(null);
//   const recorderRef = useRef(null);

//   const [lang, setLang] = useState("en");
//   const [isRecording, setIsRecording] = useState(false);
//   const [timer, setTimer] = useState(RECORD_SECONDS);
//   const [videoBlob, setVideoBlob] = useState(null);
//   const [previewUrl, setPreviewUrl] = useState("");
//   const [vitals, setVitals] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [waveGraphData, setWaveGraphData] = useState([]);

//   const t = HRV_TEXT[lang];

//   useEffect(() => {
//     if (vitals) {
//       const speechText = generateSpeechText(vitals, lang);
//       console.log(speechText, "speechText")
//       speak(speechText, lang);
//     }
//   }, [vitals, lang]);




//   const mapLabel = (label) =>
//     label === "Low"
//       ? t.LOW
//       : label === "Healthy"
//         ? t.HEALTHY
//         : t.OPTIMAL;


//   const startRecording = () => {
//     const stream = webcamRef.current?.stream;
//     if (!stream) return alert("Webcam not available");

//     // speak("Recording started. Please look at the camera and stay still.");

//     recorderRef.current = new RecordRTC(stream, {
//       type: "video",
//       mimeType: "video/mp4",
//       bitsPerSecond: 3000000,
//     });

//     recorderRef.current.startRecording();
//     setIsRecording(true);

//     let t = RECORD_SECONDS;
//     setTimer(t);

//     const interval = setInterval(() => {
//       t -= 1;

//       setTimer(t);
//       // if (t === 60) speak("Half time completed. Keep looking at the camera."); 
//       // if (t === 5) speak("Recording is about to finish. Please do not move.");
//       if (t <= 0) {
//         clearInterval(interval);
//         stopRecording();
//       }
//     }, 1000);
//   };

//   const stopRecording = () => {
//     recorderRef.current.stopRecording(() => {
//       const blob = recorderRef.current.getBlob();
//       setVideoBlob(blob);
//       setPreviewUrl(URL.createObjectURL(blob));
//       setIsRecording(false);
//       // speak("Recording complete. Review the video and submit.");
//     });
//   };


//   const getSDNNStatus = (v) => {
//     if (v < 30) return { label: "Low", active: "Low", color: "#fbbc05" };
//     if (v <= 96) return { label: "Healthy", active: "Healthy", color: "#34a853" };
//     return { label: "Optimal", active: "Optimal", color: "#0f9d58" };
//   };



//   const getRMSSDStatus = (v) => {
//     if (v < 20) return { label: "Low", active: "Low", color: "#fbbc05" };
//     if (v <= 90) return { label: "Healthy", active: "Healthy", color: "#34a853" };
//     return { label: "Optimal", active: "Optimal", color: "#0f9d58" };
//   };

//   const getLFHFStatus = (v) => {
//     if (v < 1) return { label: "Optimal", active: "Relaxed", color: "#0f9d58" };
//     if (v <= 2) return { label: "Healthy", active: "Balanced", color: "#34a853" };
//     return { label: "High Stress", active: "Stressed", color: "#ea4335" };
//   };

//   const formatNum = (v, digits = 0) =>
//     typeof v === "number" && !isNaN(v) ? v.toFixed(digits) : "--";

//     const handleSubmit = async () => {
//       if (!videoBlob) {
//         alert("No video recorded");
//         return;
//       }
    
//       const token = localStorage.getItem("token");
    
//       try {
//         setLoading(true);
    
//         /* ================= 1️⃣ UPLOAD VIDEO ================= */
    
//         const formData = new FormData();
//         formData.append("file", videoBlob);
//         formData.append(
//           "phone_number",
//           localStorage.getItem("patient_phone_number")
//         );
//         formData.append("token", token)
    
//         const vitalsRes = await fetch(
//           "https://lovencare.gopillz.com/api/vitals/video",
//           {
//             method: "POST",
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//             body: formData,
//           }
//         );
    
//         if (!vitalsRes.ok) {
//           throw new Error("Vitals API failed");
//         }
    
//         const data = await vitalsRes.json();
    
//         /* ================= 2️⃣ UPDATE UI ================= */
    
//         setWaveGraphData(
//           (data.waveform || []).map((v, i) => ({ time: i, value: v }))
//         );
    
//         setVitals({
//           heartRate: Math.round(data.heart_rate_bpm_fft || 0),
//           spo2: Math.round(data?.spo2?.spo2 || 0),
//           SDNN: data?.hrv_time?.SDNN_ms || 0,
//           RMSSD: data?.hrv_time?.RMSSD_ms || 0,
//           LFHF: data?.hrv_freq?.["LF/HF"] || 0,
//           sbp: Math.round(data?.bp_info?.sbp ?? data?.sbp ?? 0),
//           dbp: Math.round(data?.bp_info?.dbp ?? data?.dbp ?? 0),
//         });
    
//         /* ================= 3️⃣ MARK PAYMENT AS USED ================= */
    
//         // if (token) {
//         //   const paymentRes = await fetch(
//         //     "https://www.careplus.gopillz.com/scan/update_status/",
//         //     {
//         //       method: "POST",
//         //       headers: {
//         //         Authorization: `Bearer ${token}`,
//         //       },
//         //     }
//         //   );
    
//         //   if (!paymentRes.ok) {
//         //     throw new Error("Failed to update payment status");
//         //   }
//         // }
    
//         /* ================= 4️⃣ CLEANUP ================= */
    
//         localStorage.clear()
    
//       } catch (err) {
//         console.error("Scan submit error:", err);
//         alert("Scan completed but payment update failed. Please contact support.");
//       } finally {
//         setLoading(false);
//       }
//     };
    

//   const T = MEDICAL_TEXT[lang];

//   return (
//     <div className="app-bg">
//       <Navbar />

//       <div className="lang-toggle">
//         <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>English</button>
//         <button className={lang === "hi" ? "active" : ""} onClick={() => setLang("hi")}>हिन्दी</button>
//       </div>

//       <div className="container">
//         {!videoBlob && (
//           <div className="card">
//             <Webcam ref={webcamRef} />
//             {!isRecording ? (
//               <button onClick={startRecording}>▶ {T.startScan}</button>
//             ) : (
//               <h2>{timer}s</h2>
//             )}
//           </div>
//         )}

//         {videoBlob && !vitals && (
//           <div className="card">
//             <video src={previewUrl} controls />
//             <button onClick={handleSubmit}>
//               {loading ? T.analyzing : T.analyze}
//             </button>
//           </div>
//         )}

//         {vitals && (
//           <div className="card">
//             <h2>{T.report}</h2>

//             <div className="vitals-grid">
//               <div className="vital-box">❤️ {vitals.heartRate} <small>{T.hr}</small></div>
//               <div className="vital-box">🫁 {vitals.spo2}% <small>SpO₂</small></div>
//               <div className="vital-box">🩸 {vitals.sbp}/{vitals.dbp} <small>{T.bp}</small></div>
//             </div>

//             {getMedicalInsights(vitals, lang).map((m, i) => (
//               <div key={i} className={`alert ${m.type}`}>
//                 <strong>{m.title}</strong>
//                 <p>{m.text}</p>
//               </div>
//             ))}

//             {(() => {
//               const s = getSDNNStatus(vitals.SDNN);

//               return (
//                 <div className="health-card">
//                   <span className="pill" style={{ background: s.color }}>
//                     {mapLabel(s.label)}
//                   </span>

//                   <h2>{formatNum(vitals.SDNN)} <span>ms</span></h2>

//                   <div className="scale">
//                     <span className={s.active === "Low" ? "active" : ""}>{t.LOW}</span>
//                     <span className={s.active === "Healthy" ? "active" : ""}>{t.HEALTHY}</span>
//                     <span className={s.active === "Optimal" ? "active" : ""}>{t.OPTIMAL}</span>
//                   </div>

//                   <small>{t.SDNN_DESC}</small>
//                 </div>
//               );
//             })()}


//             {/* RMSSD */}
//             {(() => {
//               const s = getRMSSDStatus(vitals.RMSSD);

//               return (
//                 <div className="health-card">
//                   <span className="pill" style={{ background: s.color }}>
//                     {mapLabel(s.label)}
//                   </span>

//                   <h2>{formatNum(vitals.RMSSD)} <span>ms</span></h2>

//                   <div className="scale">
//                     <span className={s.active === "Low" ? "active" : ""}>{t.LOW}</span>
//                     <span className={s.active === "Healthy" ? "active" : ""}>{t.HEALTHY}</span>
//                     <span className={s.active === "Optimal" ? "active" : ""}>{t.OPTIMAL}</span>
//                   </div>

//                   <small>{t.RMSSD_DESC}</small>
//                 </div>
//               );
//             })()}


//             {vitals.LFHF > 0 && (() => {
//               const s = getLFHFStatus(vitals.LFHF);

//               return (
//                 <div className="health-card">
//                   <span className="pill" style={{ background: s.color }}>
//                     {s.active === "Relaxed"
//                       ? t.RELAXED
//                       : s.active === "Balanced"
//                         ? t.BALANCED
//                         : t.STRESSED}
//                   </span>

//                   <h2>{formatNum(vitals.LFHF, 2)}</h2>

//                   <div className="scale">
//                     <span className={s.active === "Relaxed" ? "active" : ""}>{t.RELAXED}</span>
//                     <span className={s.active === "Balanced" ? "active" : ""}>{t.BALANCED}</span>
//                     <span className={s.active === "Stressed" ? "active" : ""}>{t.STRESSED}</span>
//                   </div>

//                   <small>{t.LFHF_DESC}</small>
//                 </div>
//               );
//             })()}




//             <ResponsiveContainer width="100%" height={240}>
//               <LineChart data={waveGraphData}>
//                 <Line dataKey="value" dot={false} />
//                 <XAxis hide />
//                 <YAxis hide />
//                 <Tooltip />
//               </LineChart>
//             </ResponsiveContainer>

//             <div className="global-disclaimer">{T.disclaimer}</div>

//             <button onClick={() => window.location.reload()}>
//               🔁 {T.scanAgain}
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

